<template>
  <div class="ocr-page-section ocr-dashboard-page">
    <OcrPageShimmer v-if="loading && !payload" variant="dashboard" />
    <OcrApiErrorPanel
      v-else-if="error"
      :message="error"
      :retrying="loading"
      @retry="load"
    />
    <template v-else-if="payload">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        height="3"
        class="ocr-page-refresh-bar"
      />
      <header class="ocr-dashboard-header">
        <div>
          <h1 class="ocr-dashboard-title">Dashboard</h1>
          <p class="ocr-dashboard-caption">
            Read-only overview for <strong>{{ payload.tenant }}</strong> · folder
            <strong>{{ payload.folder }}</strong>. Data refreshes every
            {{ payload.cache_ttl_minutes }} minutes.
          </p>
        </div>
        <v-btn size="small" variant="outlined" class="ocr-toolbar-btn" :loading="loading" @click="load">
          Refresh
        </v-btn>
      </header>

      <v-alert
        v-if="payload._fallback"
        type="info"
        variant="tonal"
        density="compact"
        class="ocr-dash-fallback-alert"
      >
        Full dashboard charts need OCR API <code>/dashboard/data</code>. Showing summary counts only — redeploy
        the latest OCR image for full parity.
      </v-alert>

      <div class="ocr-tabbed-stack">
        <OcrTabs v-model="dashTab">
          <v-tab value="overview">Overview</v-tab>
          <v-tab value="files">Files</v-tab>
          <v-tab value="attributes">Attributes</v-tab>
          <v-tab value="analysis">Analysis</v-tab>
        </OcrTabs>

        <v-window v-model="dashTab" class="ocr-dash-window ocr-tab-window">
        <v-window-item value="overview">
          <div class="ocr-summary-cards">
            <div v-for="card in overviewKpis" :key="card.label" class="ocr-summary-card">
              <span class="ocr-summary-card-value">{{ card.value }}</span>
              <span class="ocr-summary-card-label">{{ card.label }}</span>
            </div>
          </div>

          <div v-if="payload.overview.processing_now" class="ocr-panel ocr-panel--processing">
            <div class="ocr-panel-title">Processing now</div>
            <p class="ocr-panel-lead ocr-panel-lead--flush">
              Status: {{ payload.overview.processing_now.status }}
              <span v-if="payload.overview.processing_now.message"> — {{ payload.overview.processing_now.message }}</span>
            </p>
          </div>

          <div class="ocr-dash-charts-row">
            <div class="ocr-dash-chart-panel">
              <div class="ocr-dash-chart-title">Processing trend</div>
              <p class="ocr-dash-chart-caption">Files completed per day</p>
              <OcrPlotlyChart v-if="trendChart.data.length" :data="trendChart.data" :layout="trendChart.layout" />
              <p v-else class="ocr-empty-cell">No processed files yet.</p>
            </div>
            <div class="ocr-dash-chart-panel">
              <div class="ocr-dash-chart-title">Files by status</div>
              <OcrPlotlyChart v-if="statusPie.data.length" :data="statusPie.data" :layout="statusPie.layout" />
              <p v-else class="ocr-empty-cell">No files yet.</p>
            </div>
          </div>

          <div class="ocr-dash-charts-row ocr-dash-charts-row--activity">
            <div class="ocr-dash-chart-panel ocr-dash-chart-panel--activity">
              <div class="ocr-dash-chart-title">Recent activity</div>
              <p class="ocr-dash-chart-caption">Latest files by last change</p>
              <OcrTableWrap class="ocr-table-wrap--inline">
                <v-table class="well-table sample-table well-table-data fd-table">
                  <thead>
                    <tr class="main-header">
                      <th>File</th>
                      <th>Status</th>
                      <th>Pages</th>
                      <th>Tables</th>
                      <th>OCR confidence</th>
                      <th>Last modified</th>
                    </tr>
                  </thead>
                  <tbody class="well-table-content">
                    <tr v-for="row in payload.overview.recent_files" :key="row.file">
                      <td>{{ row.file }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ fmtNum(row.pages) }}</td>
                      <td>{{ fmtNum(row.tables) }}</td>
                      <td>{{ fmtPct(row.ocr_confidence) }}</td>
                      <td>{{ fmtDate(row.last_modified) }}</td>
                    </tr>
                    <tr v-if="!payload.overview.recent_files.length">
                      <td colspan="6" class="ocr-empty-cell">No files yet. Upload on File Upload.</td>
                    </tr>
                  </tbody>
                </v-table>
              </OcrTableWrap>
            </div>
            <div class="ocr-dash-chart-panel ocr-dash-memory-panel">
              <div class="ocr-dash-chart-title">Session &amp; memory</div>
              <v-progress-linear
                :model-value="payload.overview.memory.system_memory_percent"
                height="8"
                rounded
                color="primary"
                class="ocr-memory-bar"
              />
              <p class="ocr-dash-chart-caption ocr-dash-chart-caption--tight">
                System: {{ payload.overview.memory.system_memory_percent }}% used ·
                {{ payload.overview.memory.system_memory_available_gb }} GB free
              </p>
              <div class="ocr-dash-memory-metrics">
                <div class="ocr-dash-memory-metric">
                  <span class="ocr-dash-memory-metric-value">{{ fmtMem(payload.overview.memory.session_size_mb) }}</span>
                  <span class="ocr-dash-memory-metric-label">Session size</span>
                </div>
                <div class="ocr-dash-memory-metric">
                  <span class="ocr-dash-memory-metric-value">{{ payload.overview.memory.files_in_memory ?? 0 }}</span>
                  <span class="ocr-dash-memory-metric-label">Files in memory</span>
                </div>
                <div class="ocr-dash-memory-metric">
                  <span class="ocr-dash-memory-metric-value">{{ fmtMem(payload.overview.memory.process_memory_mb) }}</span>
                  <span class="ocr-dash-memory-metric-label">App memory (MB)</span>
                </div>
              </div>
            </div>
          </div>
        </v-window-item>

        <v-window-item value="files">
          <div class="ocr-summary-cards">
            <div v-for="card in filesKpis" :key="card.label" class="ocr-summary-card">
              <span class="ocr-summary-card-value">{{ card.value }}</span>
              <span class="ocr-summary-card-label">{{ card.label }}</span>
            </div>
          </div>
          <div class="ocr-panel">
            <div class="ocr-files-filters">
              <v-text-field
                v-model="fileSearch"
                placeholder="Search by file name"
                variant="outlined"
                density="compact"
                hide-details
                prepend-inner-icon="mdi-magnify"
              />
              <v-select
                v-model="fileStatusFilter"
                :items="fileStatusOptions"
                placeholder="All statuses"
                variant="outlined"
                density="compact"
                hide-details
                multiple
                chips
                closable-chips
              />
            </div>
            <OcrTableWrap class="mt-3">
              <v-table class="well-table sample-table well-table-data fd-table">
                <thead>
                  <tr class="main-header">
                    <th>File</th>
                    <th>Status</th>
                    <th>Pages</th>
                    <th>Tables</th>
                    <th>Mapped</th>
                    <th>OCR confidence</th>
                    <th>Size (KB)</th>
                    <th>Uploaded</th>
                  </tr>
                </thead>
                <tbody class="well-table-content">
                  <tr v-for="row in filteredFiles" :key="row.file">
                    <td>{{ row.file }}</td>
                    <td>{{ row.status }}</td>
                    <td>{{ row.pages ?? "—" }}</td>
                    <td>{{ row.tables ?? "—" }}</td>
                    <td>{{ row.mapped_tables ?? "—" }}</td>
                    <td>{{ fmtPct(row.ocr_confidence) }}</td>
                    <td>{{ row.size_kb ?? "—" }}</td>
                    <td>{{ fmtDate(row.uploaded) }}</td>
                  </tr>
                  <tr v-if="!filteredFiles.length">
                    <td colspan="8" class="ocr-empty-cell">No files match.</td>
                  </tr>
                </tbody>
              </v-table>
            </OcrTableWrap>
            <p class="ocr-dash-chart-caption mt-2">{{ filteredFiles.length }} of {{ payload.files.length }} files</p>
          </div>
        </v-window-item>

        <v-window-item value="attributes">
          <div v-if="!payload.reported_documents.length" class="ocr-panel">
            <p class="ocr-empty-cell">No extracted data yet. Attributes appear once a file has been processed.</p>
          </div>
          <template v-else>
            <div class="ocr-panel">
              <div class="ocr-field">
                <label class="profile-menu-label">Document</label>
                <v-select
                  v-model="selectedDoc"
                  :items="payload.reported_documents"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  @update:model-value="loadReportDetail"
                />
              </div>
            </div>
            <OcrPageShimmer v-if="reportLoading" variant="attributes" />
            <template v-else-if="reportDetail">
              <div class="ocr-summary-cards">
                <div v-for="card in attributeKpis" :key="card.label" class="ocr-summary-card">
                  <span class="ocr-summary-card-value">{{ card.value }}</span>
                  <span class="ocr-summary-card-label">{{ card.label }}</span>
                </div>
              </div>
              <div class="ocr-dash-attributes-grid">
                <OcrTableWrap title="Report details">
                  <v-table class="well-table sample-table well-table-data fd-table">
                    <tbody class="well-table-content">
                      <tr v-for="row in reportDetail.details" :key="row.field">
                        <td>{{ row.field }}</td>
                        <td>{{ row.value }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </OcrTableWrap>
                <OcrTableWrap title="Extracted tables">
                  <v-table class="well-table sample-table well-table-data fd-table">
                    <thead>
                      <tr class="main-header">
                        <th>#</th>
                        <th>Page</th>
                        <th>Table type</th>
                        <th>Status</th>
                        <th>Rows</th>
                        <th>Columns</th>
                      </tr>
                    </thead>
                    <tbody class="well-table-content">
                      <tr
                        v-for="t in reportDetail.tables"
                        :key="t.index"
                        class="ocr-table-pick-row"
                        :class="{ 'ocr-table-pick-row--active': pickedTableIndex === t.index }"
                        @click="pickedTableIndex = t.index"
                      >
                        <td>{{ t.index + 1 }}</td>
                        <td>{{ t.page }}</td>
                        <td>{{ t.table_type }}</td>
                        <td>{{ t.status }}</td>
                        <td>{{ t.rows }}</td>
                        <td>{{ t.columns }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </OcrTableWrap>
              </div>
              <div v-if="pickedTable">
                <OcrTabs v-model="tableVersionTab">
                  <v-tab v-for="v in pickedTable.versions" :key="v.key" :value="v.key">{{ v.label }}</v-tab>
                </OcrTabs>
                <OcrTableWrap title="Table data (read-only)" class="ocr-table-wrap--titled-spaced">
                  <v-table class="well-table sample-table well-table-data fd-table">
                    <tbody class="well-table-content">
                      <tr v-for="(row, ri) in tableMatrixRows" :key="ri">
                        <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </OcrTableWrap>
              </div>
            </template>
          </template>
        </v-window-item>

        <v-window-item value="analysis">
          <div v-if="!payload.reported_documents.length" class="ocr-panel">
            <p class="ocr-empty-cell">No processed files to analyse yet.</p>
          </div>
          <template v-else>
            <div class="ocr-summary-cards">
              <div v-for="card in analysisKpis" :key="card.label" class="ocr-summary-card">
                <span class="ocr-summary-card-value">{{ card.value }}</span>
                <span class="ocr-summary-card-label">{{ card.label }}</span>
              </div>
            </div>
            <div class="ocr-dash-charts-row">
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">OCR confidence by page</div>
                <OcrPlotlyChart
                  v-if="pageConfChart.data.length"
                  :data="pageConfChart.data"
                  :layout="pageConfChart.layout"
                />
                <p v-else class="ocr-empty-cell">No OCR word data for these files.</p>
              </div>
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">Tables by type</div>
                <OcrPlotlyChart
                  v-if="tablesTypeChart.data.length"
                  :data="tablesTypeChart.data"
                  :layout="tablesTypeChart.layout"
                />
                <p v-else class="ocr-empty-cell">No tables extracted yet.</p>
              </div>
            </div>
            <div class="ocr-dash-charts-row">
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">Tables per page</div>
                <OcrPlotlyChart
                  v-if="tablesPageChart.data.length"
                  :data="tablesPageChart.data"
                  :layout="tablesPageChart.layout"
                />
              </div>
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">Table status</div>
                <OcrPlotlyChart
                  v-if="tableStatusChart.data.length"
                  :data="tableStatusChart.data"
                  :layout="tableStatusChart.layout"
                />
              </div>
            </div>
            <div class="ocr-dash-charts-row">
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">Word confidence distribution</div>
                <OcrPlotlyChart
                  v-if="wordHistChart.data.length"
                  :data="wordHistChart.data"
                  :layout="wordHistChart.layout"
                />
              </div>
              <div class="ocr-dash-chart-panel">
                <div class="ocr-dash-chart-title">Pages needing manual review</div>
                <OcrTableWrap>
                  <v-table class="well-table sample-table well-table-data fd-table">
                    <thead>
                      <tr class="main-header">
                        <th>File</th>
                        <th>Page</th>
                        <th>Words</th>
                        <th>Mean confidence</th>
                      </tr>
                    </thead>
                    <tbody class="well-table-content">
                      <tr v-for="(row, i) in payload.analysis.review_pages" :key="i">
                        <td>{{ row.file }}</td>
                        <td>{{ row.page }}</td>
                        <td>{{ row.words }}</td>
                        <td>{{ fmtPct(row.mean_confidence) }}</td>
                      </tr>
                      <tr v-if="!payload.analysis.review_pages.length">
                        <td colspan="4" class="ocr-empty-cell">All pages above the review threshold.</td>
                      </tr>
                    </tbody>
                  </v-table>
                </OcrTableWrap>
              </div>
            </div>
          </template>
        </v-window-item>
        </v-window>
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import { fetchDashboardPayload } from "@/services/ocrDashboardApi";
import OcrPageShimmer from "@/components/layout/OCR/OcrPageShimmer.vue";
import OcrApiErrorPanel from "@/components/layout/OCR/OcrApiErrorPanel.vue";
import OcrPlotlyChart from "@/components/layout/OCR/OcrPlotlyChart.vue";
import OcrTabs from "@/components/layout/OCR/OcrTabs.vue";
import OcrTableWrap from "@/components/layout/OCR/OcrTableWrap.vue";

const STATUS_COLORS = {
  Processed: "#2E7D32",
  Processing: "#1E88E5",
  Waiting: "#90A4AE",
  Queued: "#F9A825",
  Failed: "#C62828",
};
const BLUE = "#1E88E5";

export default {
  name: "OcrDashboard",
  components: { OcrPageShimmer, OcrApiErrorPanel, OcrPlotlyChart, OcrTabs, OcrTableWrap },
  inject: {
    ocrContextKey: { default: () => () => 0 },
  },
  data() {
    return {
      loading: true,
      error: null,
      payload: null,
      dashTab: "overview",
      fileSearch: "",
      fileStatusFilter: [],
      selectedDoc: null,
      reportDetail: null,
      reportLoading: false,
      pickedTableIndex: 0,
      tableVersionTab: "table_data_saved",
    };
  },
  computed: {
    folderEpoch() {
      return this.ocrContextKey();
    },
    overviewKpis() {
      const k = this.payload?.overview?.kpis || {};
      return [
        { label: "Files processed", value: k.files_processed ?? 0 },
        { label: "Pages digitized", value: (k.pages_digitized ?? 0).toLocaleString() },
        { label: "Avg OCR confidence", value: this.fmtPct(k.avg_ocr_confidence) },
        {
          label: "Waiting / failed",
          value: `${k.waiting ?? 0} / ${k.failed ?? 0}`,
        },
      ];
    },
    filesKpis() {
      const files = this.payload?.files || [];
      const processed = files.filter((f) => f.status === "Processed");
      return [
        { label: "Total files", value: files.length },
        { label: "Processed", value: processed.length },
        {
          label: "Waiting",
          value: files.filter((f) => ["Waiting", "Queued", "Processing"].includes(f.status)).length,
        },
        { label: "Failed", value: files.filter((f) => f.status === "Failed").length },
      ];
    },
    fileStatusOptions() {
      return [...new Set((this.payload?.files || []).map((f) => f.status))].sort();
    },
    filteredFiles() {
      let rows = this.payload?.files || [];
      const q = this.fileSearch.trim().toLowerCase();
      if (q) rows = rows.filter((f) => f.file.toLowerCase().includes(q));
      if (this.fileStatusFilter.length) {
        rows = rows.filter((f) => this.fileStatusFilter.includes(f.status));
      }
      return rows;
    },
    analysisKpis() {
      const a = this.payload?.analysis || {};
      return [
        { label: "Files analysed", value: a.files_analysed ?? 0 },
        { label: "Words recognised", value: (a.words_recognised ?? 0).toLocaleString() },
        { label: "Median page confidence", value: this.fmtPct(a.median_page_confidence) },
        {
          label: "Pages needing review",
          value: a.pages_needing_review ?? 0,
        },
      ];
    },
    attributeKpis() {
      const s = this.reportDetail?.summary || {};
      return [
        { label: "Pages", value: s.pages ?? "—" },
        { label: "Tables extracted", value: s.tables ?? "—" },
        { label: "Tables mapped", value: s.mapped_tables ?? "—" },
        { label: "OCR confidence", value: this.fmtPct(s.ocr_confidence) },
      ];
    },
    pickedTable() {
      if (!this.reportDetail?.tables?.length) return null;
      return this.reportDetail.tables.find((t) => t.index === this.pickedTableIndex) || this.reportDetail.tables[0];
    },
    tableMatrixRows() {
      const raw = this.reportDetail?.raw_tables?.[this.pickedTableIndex];
      if (!raw || !this.tableVersionTab) return [];
      const matrix = raw[this.tableVersionTab];
      const asRows = (rows) =>
        (rows || []).map((row) => (Array.isArray(row) ? row : Object.values(row || {})));
      if (Array.isArray(matrix)) return asRows(matrix);
      if (matrix && Array.isArray(matrix.data)) return asRows(matrix.data);
      return [];
    },
    trendChart() {
      const trend = this.payload?.overview?.processing_trend || [];
      return {
        data: trend.length
          ? [{ type: "bar", x: trend.map((t) => t.day), y: trend.map((t) => t.files), marker: { color: BLUE } }]
          : [],
        layout: { xaxis: { title: "" }, yaxis: { title: "", dtick: 1 } },
      };
    },
    statusPie() {
      const sc = this.payload?.overview?.status_counts || [];
      return {
        data: sc.length
          ? [
              {
                type: "pie",
                labels: sc.map((s) => s.status),
                values: sc.map((s) => s.count),
                hole: 0.55,
                marker: { colors: sc.map((s) => STATUS_COLORS[s.status] || "#90A4AE") },
              },
            ]
          : [],
        layout: { showlegend: true, legend: { orientation: "h", y: -0.15 } },
      };
    },
    pageConfChart() {
      const rows = this.payload?.analysis?.page_confidence || [];
      const multi = (this.payload?.analysis?.files_analysed || 0) > 1;
      return {
        data: rows.length
          ? [
              {
                type: "bar",
                x: rows.map((r) => r.page),
                y: rows.map((r) => r.mean_confidence),
                ...(multi ? { name: "Pages" } : {}),
                marker: { color: BLUE },
              },
            ]
          : [],
        layout: {
          yaxis: { title: "Mean confidence (%)", range: [0, 100] },
          shapes: [
            {
              type: "line",
              x0: 0,
              x1: 1,
              xref: "paper",
              y0: this.payload?.analysis?.review_threshold ?? 85,
              y1: this.payload?.analysis?.review_threshold ?? 85,
              line: { dash: "dash", color: "#F9A825" },
            },
          ],
        },
      };
    },
    tablesTypeChart() {
      const rows = this.payload?.analysis?.tables_by_type || [];
      return {
        data: rows.length
          ? [
              {
                type: "bar",
                orientation: "h",
                y: rows.map((r) => r.table_type),
                x: rows.map((r) => r.tables),
                marker: { color: BLUE },
              },
            ]
          : [],
        layout: { xaxis: { title: "" }, yaxis: { title: "" } },
      };
    },
    tablesPageChart() {
      const rows = this.payload?.analysis?.tables_per_page || [];
      return {
        data: rows.length
          ? [{ type: "bar", x: rows.map((r) => r.page), y: rows.map((r) => r.tables), marker: { color: "#2E7D32" } }]
          : [],
        layout: { xaxis: { dtick: 1 } },
      };
    },
    tableStatusChart() {
      const rows = this.payload?.analysis?.table_status || [];
      return {
        data: rows.length
          ? [
              {
                type: "pie",
                labels: rows.map((r) => r.status),
                values: rows.map((r) => r.tables),
                hole: 0.55,
              },
            ]
          : [],
        layout: { showlegend: true },
      };
    },
    wordHistChart() {
      const h = this.payload?.analysis?.word_confidence_histogram;
      if (!h?.counts?.length) return { data: [], layout: {} };
      const edges = h.bin_edges || [];
      const x = edges.slice(0, -1).map((e, i) => (e + edges[i + 1]) / 2);
      return {
        data: [{ type: "bar", x, y: h.counts, marker: { color: BLUE } }],
        layout: {
          xaxis: { title: "Confidence (%)" },
          yaxis: { title: "Words" },
          shapes: [
            {
              type: "line",
              x0: this.payload?.analysis?.review_threshold ?? 85,
              x1: this.payload?.analysis?.review_threshold ?? 85,
              y0: 0,
              y1: 1,
              yref: "paper",
              line: { dash: "dash", color: "#F9A825" },
            },
          ],
        },
      };
    },
  },
  watch: {
    pickedTable(val) {
      if (val?.versions?.length) {
        this.tableVersionTab = val.versions[0].key;
      }
    },
    folderEpoch(newVal, oldVal) {
      if (oldVal === undefined || newVal === oldVal) return;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmtPct(v) {
      if (v == null || v === "" || Number.isNaN(Number(v))) return "";
      return `${Number(v).toFixed(1)}%`;
    },
    fmtNum(v) {
      if (v == null || v === "") return "";
      return v;
    },
    fmtMem(v) {
      if (v == null || v === "" || Number.isNaN(Number(v))) return "";
      return Number(v).toFixed(1);
    },
    fmtDate(v) {
      if (!v) return "";
      try {
        return new Date(v).toLocaleString();
      } catch {
        return v;
      }
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await fetchDashboardPayload();
        this.payload = data;
        if (data.reported_documents?.length && !this.selectedDoc) {
          this.selectedDoc = data.reported_documents[0];
          this.loadReportDetail();
        }
      } catch (e) {
        this.payload = null;
        this.error = formatOcrApiError(e, "Could not load dashboard.");
      } finally {
        this.loading = false;
      }
    },
    async loadReportDetail() {
      if (!this.selectedDoc) return;
      this.reportLoading = true;
      try {
        const { data } = await ocrApi.get("api/v1/dashboard/report", {
          params: { filename: this.selectedDoc },
        });
        this.reportDetail = data;
        this.pickedTableIndex = 0;
        if (data.tables?.[0]?.versions?.length) {
          this.tableVersionTab = data.tables[0].versions[0].key;
        }
      } catch (e) {
        this.reportDetail = null;
      } finally {
        this.reportLoading = false;
      }
    },
  },
};
</script>
